/**
 * Full Sudoku Board including Settings about game play.
 */
export class SudokuGame {
    /**
    * Render the answer cells into the board when this flag is set
    * @type {boolean}
    */
    showSolution = false;

    /**
     * When true incorrect moves will be displayed in red
     * @type {boolean}
     */
    showIncorrectMoves = true;

    /**
     * If true, when a guess is added to a cell, that number will be removed from the possibilities in the shared rows, columns and boxes.
     * @type {boolean}
     */
    automaticallyRemovePossibilities = false;

    /**
     * Shows the possibilities in grid format
     * @type {boolean}
     */
    possibilitiesInGrid = false;

    /**
     * If true, highlight each number in the cell based on the active number
     * @type {boolean}
     */
    highlightActiveNumber = true;

    /**
     * The number that is currently active for the user
     * @type {string}
     */
    activeNumber: string | null = null;

    /**
     * The current active cell
     * @type {Cell}
     */
    activeCell: Cell | null = null;

    /**
     * If true, highlight the active cell
     * @type {boolean}
     */
    highlightActiveCell = false;

    /**
     * The game cells
     * @type {Cell[]}
     */
    cells: Cell[] = [];

    /**
     * The max undos allowed
     */
    readonly MAX_UNDOS = 200;

    /**
     * The set of valid inputs
     * @type {Set<string>}
     */
    validInputs: Set<string> = new Set();

    /**
     * The size of the game (example size = 9 would mean a 9x9 board)
     * @type {number}
     */
    size: number;

    /**
     * The undos for the game
     * @type {string[]}
     */
    undos: string[] = [];

    /**
     * 
     * @param {number} size - The size of the game
     */
    constructor(size: number, solution?: string[] | number[], startingPosition?: string[] | number[]) {
        this.size = size;
        // create an array the game cells
        for (var i = 0; i < size * size; i++) {
            const cell = new Cell();
            const row = Math.floor(i / size)
            const col = i % size;
            let boxWidth = this.getBoxColumnCount()
            let boxHeight = this.getBoxRowCount()
            let boxRow = Math.floor(row / boxHeight);
            let boxColumn = Math.floor(col / boxWidth);
            cell.id = i;
            cell.row = row;
            cell.column = col;
            cell.box = boxRow * (size / boxWidth) + boxColumn

            this.cells.push(cell);
        }

        const possibilites = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F', 'G'];
        for (var i = 0; i < size; i++) {
            this.validInputs.add(possibilites[i])
        }

         // Set cell values 
         if (solution && startingPosition) {
            this.cells.forEach((cell, idx) => {
                const solValue = solution[idx].toString();
                const startVal = startingPosition[idx].toString();

                cell.answer = solValue;
                if (startVal != '0') {
                    cell.guess = solValue;
                    cell.isGiven = true;

                }
            })
        }
    }

    /**
     * Save the current state of the game
     */
    saveUndo() {
        // Check if the current state of the game is the same as the last undo
        var newUndo = this.serialize();
        const previousUndo = this.undos[this.undos.length - 1];
        if (newUndo == previousUndo) {
            return;
        }
        this.undos.push(newUndo)
    }

    /** Set the active cell */
    setActiveCell(cellId) {
        const cell = this.cells[cellId];
        this.activeCell = cell;
    }
    /**
     * Change the active cell to the one in the direction specified
     * @param {string} direction - "up", "down", "left", "right" 
     */
    moveActiveCell(direction) {

        // If no active cell, just return
        if (!this.activeCell) {
            return;
        }

        var row = this.activeCell.row;
        var col = this.activeCell.column;

        if (direction == "up") {
            row--;
        } else if (direction == "down") {
            row++;
        } else if (direction == "left") {
            col--;
        } else if (direction == "right") {
            col++;
        }
        // Check if the new cell is out of bounds
        if (row < 0 || row >= this.size || col < 0 || col >= this.size) {
            return;
        }
        var newCell = this.cells[row * this.size + col];
        this.activeCell = newCell;
    }

    /**
     * Returns an array of numbers that have been completely used in the puzzle
     * @returns {string[]}
     */
    checkUsedNumbers() {
        const finished: string[] = [];
        this.cells.reduce((acc, cell) => {
            const guess = cell.guess;
            if (guess) {
                if (!acc[guess]) {
                    acc[guess] = 0;
                }
                acc[guess]++;
                // Check if we have used all the numbers
                if (acc[guess] == this.size) {
                    finished.push(guess)
                }
            }
            return acc
        }, {})
        return finished;
    }

    /**
     * Return true if all cells are filled in and correct
     */
    isComplete() {
        return this.cells.every(cell => cell.isCorrect())
    }

    /**
     * Returns true if any of the filled in cells is incorrect.
     */
    hasErrors() {
        return this.cells.some((cell) => cell.isWrong())
    }

    serialize() {
        return this.cells.map(cell => cell.serialize()).join('|')
    }

    /**
     * Checks if input is a valid character to enter in the game
     * @param {any} input
     */
    isValidInput(input) {
        return this.validInputs.has(input);
    }

    /**
     * Add a possibility to a cell, or remove it if the cell already contains the possibility.
     * @param {number} cellId The cell index
     * @param {string} possibility - the possibility
     */
    togglePossibility(cellId, possibility) {
        possibility = possibility.toString();
        // Dont allow invalid input
        if (!this.isValidInput(possibility)) {
            return;
        }

        const cell = this.cells[cellId];
        // Clear a guess if this isn't a given cell
        if (!cell.isGiven) {
            cell.guess = null;
        }
        cell.togglePossibility(possibility)
    }

    /**
     * Add a guess to a cell 
     * @param {number} cellId
     * @param {string} guess
     * @returns
     */
    addGuess(cellId, guess) {
        // convert to a string so we don't have '1' and 1 in the list of possibilities
        guess = guess.toString();
        // Dont allow invalid input
        if (!this.isValidInput(guess)) {
            return;
        }
        const theCell = this.cells[cellId]

        // Don't edit given cells
        if (theCell.isGiven) {
            return;
        }

        // Don't allow the user to update the board during show solution
        if (this.showSolution) {
            return;
        }

        // if the user is adding the same value as is currently in guess,
        // then just remove the guess
        if (theCell.guess == guess) {
            theCell.guess = null;
            // return so we don't automatically remove possibilities
            return;
        }

        let result = theCell.addGuess(guess);

        if (this.automaticallyRemovePossibilities) {
            this.removeRelatedPossibilities(cellId, guess)
        }
        return result;
    }

    /**
     * Remove the possibility from all cells that share a row, column or box with the cell at cellId
     * @param {number} cellId - the cell index
     * @param {string} possibility
     */
    removeRelatedPossibilities(cellId, possibility) {
        const relatedCells = this.getAllRelatedCells(cellId)
        relatedCells.forEach((cell) => {
            cell.removePossibility(possibility);
        })
    }

    /**
     * For a given cellId, returs all of the cells that share a row, column or box with that cell, including the cell itself.
     * @param {number} cellId - the cell index
     * @returns {Cell[]}
     */
    getAllRelatedCells(cellId) {
        return [...this.getCellsInSameRow(cellId), ...this.getCellsInSameColumn(cellId), ...this.getCellsInSameBox(cellId)];
    }

    /**
     * Get all the cells in the same row as the cell id.
     * @param {number} cellId
     * @returns {Cell[]}
     */
    getCellsInSameRow(cellId, excludeThisCell = false) {
        const cell = this.getCell(cellId);
        return this.cells.filter(c => c.row == cell.row && (!excludeThisCell || c != cell));
    }

    /**
     * Get all the cells in the same column as the cell id
     * @param {number} cellId
     * @returns {Cell[]}
     */
    getCellsInSameColumn(cellId, excludeThisCell = false) {
        const cell = this.getCell(cellId);
        return this.cells.filter(c => c.column == cell.column && (!excludeThisCell || c != cell));
    }

    /**
     * Get all the cells in the same box as the cell id
     * @param {number} cellId
     * @returns {Cell[]}
     */
    getCellsInSameBox(cellId, excludeThisCell = false) {
        const cell = this.getCell(cellId);
        return this.cells.filter(c => c.box == cell.box && (!excludeThisCell || c != cell));
    }

    /**
     * Gets the number of columns in a box (the 6x6 game is a special case where the boxes are not square)
     * @returns
     */
    getBoxColumnCount() {
        if (this.size == 6) {
            return 2;
        }
        return Math.sqrt(this.size);
    }
    /**
     * Gets the number of rows in a box. (the 6x6 game is a special case where the boxes are not square)
     * @returns
     */
    getBoxRowCount() {
        if (this.size == 6) {
            return 3;
        }
        return Math.sqrt(this.size)
    }

    /**
     * Clear the cell
     * @param {number} cellId
     */
    clearCell(cellId) {
        if (this.cells[cellId]) {
            this.cells[cellId].clear()
        }
        
    }
    /**
     * Reset the game to the starting position
     */
    reset() {
        this.cells.forEach(cell => cell.clear())
    }
    /**
     * Remove all possibilities
     */
    resetPossibilities() {
        this.cells.forEach(cell => cell.resetPossibilities())
    }
    /**
     * Undo the last move
     */
    undo() {
        const theUndo = this.undos.pop();
        this.restoreGame(theUndo)
    }

    /**
     * Set the game to the value specified in game string
     * @param {string} gameString
     * @example 1|||3|P123|
     */
    restoreGame(gameString) {
        const cellValues = gameString.split("|")
        cellValues.forEach((cellValue, idx) => {
            const theCell = this.getCell(idx)
            // Old saved games had extra trailing space
            if (!theCell) {
                return;
            }
            theCell.clear()
            if (cellValue[0] == "P") {
                cellValue = cellValue.substring(1)
                cellValue.split("").forEach((val) => {
                    if (val.trim() != "") {
                        theCell.togglePossibility(val.trim())
                    }
                })
            } else {
                theCell.addGuess(cellValue.trim())
            }
        })
    }
    /**
     * Gets the rows
     * @returns {Cell[][]}
     */
    getRows() {
        return this.cells.reduce<Cell[][]>((acc, cell, idx) => {
            if (!acc[cell.row]) {
                acc[cell.row] = [];
            }
            acc[cell.row].push(cell);
            return acc;
        }, [])

    }
    /**
     * Gets the columns for the game
     * @returns {Cell[][]}
     */
    getColumns() {
        return this.cells.reduce<Cell[][]>((acc, cell, idx) => {
            if (!acc[cell.column]) {
                acc[cell.column] = [];
            }
            acc[cell.column].push(cell);
            return acc;
        }, [])

    }

    /**
     * Gets the boxes for the game - index 0 in the array is the box in the top left corner of the game.
     * @returns {Cell[][]}
     */
    getBoxes() {
        return this.cells.reduce<Cell[][]>((acc, cell, idx) => {
            if (!acc[cell.box]) {
                acc[cell.box] = [];
            }
            acc[cell.box].push(cell);
            return acc;
        }, [])

    }

    /**
     * gets a cell
     * @param {number} id
     * @returns {Cell}
     */
    getCell(id) {
        return this.cells[id]
    }

    /**
     * Update the game goard on the page
     */
    updateGameBoardUI() {
        const activeCellId = this.activeCell ? this.activeCell.id : null;
        $("#sudokutable > tbody > tr > td").each((idx, td) => {
            let index = parseInt(td.id.substring(1));
            td.innerHTML = this.getCell(index).render({
                possibilitiesInGrid: this.possibilitiesInGrid,
                showIncorrectMoves: this.showIncorrectMoves,
                showSolution: this.showSolution,
                gridBoxHeight: this.getBoxRowCount(),
                gridBoxWidth: this.getBoxColumnCount(),
                activeNumber: this.activeNumber,
                highlightActiveNumber: this.highlightActiveNumber,
                activeCellId: activeCellId,
                highlightActiveCell: this.highlightActiveCell
            });
        })
    }

    /**
     * Does the game have any moves to undo
     * @returns
     */
    hasUndos() {
        return this.undos.length > 0
    }

    /**
     * Determines each cells possible values by finding all of the related cells with values and 
     * removing them from the possibilities.
     */
    calculateAllPossibilities() {
        this.cells.forEach((cell, idx) => {
            // If a cell is already filled in then assume it has no possibilities.
            if (cell.guess) {
                cell.solverPossibilites = new Set();
            } else {
                const usedNumbers = this.getAllRelatedCells(idx).reduce((used, relatedCell) => {
                    if (relatedCell.guess) {
                        used.add(relatedCell.guess)
                    }
                    return used;
                }, new Set())
                cell.solverPossibilites = this.validInputs.difference(usedNumbers);
            }
        })
    }

    /**
    * Returns the union of all of the possibilites for a set of cells
    * @param {Cell[]} cells
    */
    getPossibilitiesUnionForCells(cells) {
        return cells.reduce((acc, cell) => {
            return acc.union(cell.solverPossibilites)
        }, new Set())
    }

    /**
     * Iterates over all cells in the game board and finds any cells that only have a single possibilitity remaining.
     * Returns true if it updated cells, false otherwise
     */
    strategyFindSoleRemainingPossibility() {
        for (var i = 0; i < this.cells.length; i++) {
            const cell = this.cells[i];
            if (!cell.guess && cell.solverPossibilites.size == 1) {
                return {
                    id: i,
                    answer: cell.solverPossibilites.values().next().value,
                    strategy: "strategyFindSoleRemainingPossibility"
                }
            }
            return null;
        }
    }
    /**
     * For each row find any cell that is the sole possibility for a number.
     * As an example if row 0, has only a single cell with the possibility of a 7, 
     * this strategy would find that cell
     * @returns
     */
    strategyOnlyOptionInRow() {
        const rows = this.getRows();
        for (var i = 0; i < rows.length; i++) {
            const row = rows[i];
            for (var j = 0; j < row.length; j++) {
                const cell = row[j];
                if (!cell.guess) {
                    const otherCells = this.getCellsInSameRow(cell.id, true);
                    const otherPossibilities = this.getPossibilitiesUnionForCells(otherCells);
                    const diff = cell.solverPossibilites.difference(otherPossibilities);
                    if (diff.size == 1) {
                        return {
                            id: cell.id,
                            answer: diff.values().next().value,
                            strategy: "strategyOnlyOptionInRow"
                        }
                    }
                }
            }
        }
        return null;
    }
    /**
      * For each column find any cell that is the sole possibility for a number.
      * As an example if column 0, has only a single cell with the possibility of a 7, 
      * this strategy would find that cell
      * @returns
      */
    strategyOnlyOptionInColumn() {
        const columns = this.getColumns();
        for (var i = 0; i < columns.length; i++) {
            const column = columns[i];
            for (var j = 0; j < column.length; j++) {
                const cell = column[j];
                if (!cell.guess) {
                    const otherCells = this.getCellsInSameColumn(cell.id, true);
                    const otherPossibilities = this.getPossibilitiesUnionForCells(otherCells);
                    const diff = cell.solverPossibilites.difference(otherPossibilities);
                    if (diff.size == 1) {
                        return {
                            id: cell.id,
                            answer: diff.values().next().value,
                            strategy: "strategyOnlyOptionInColumn"
                        }
                    }
                }
            }
        }
        return null;
    }

    /**
      * For each box find any cell that is the sole possibility for a number.
      * As an example if box 0, has only a single cell with the possibility of a 7, 
      * this strategy would find that cell
      * @returns
      */
    strategyOnlyOptionInBox() {
        const boxes = this.getBoxes();
        for (var i = 0; i < boxes.length; i++) {
            const box = boxes[i];
            for (var j = 0; j < box.length; j++) {
                const cell = box[j];
                if (!cell.guess) {
                    const otherCells = this.getCellsInSameBox(cell.id, true);
                    const otherPossibilities = this.getPossibilitiesUnionForCells(otherCells);
                    const diff = cell.solverPossibilites.difference(otherPossibilities);
                    if (diff.size == 1) {
                        return {
                            id: cell.id,
                            answer: diff.values().next().value,
                            strategy: "strategyOnlyOptionInBox"
                        }
                    }
                }
            }
        }
        return null;
    }

    /**
     * Iterate over the list of strategies attempting to fill in the puzzle.
     * If progress is made by one of the strategies, the solve function reiterates through 
     * the list of strategies, because prior strategies may work once a number is found.
     */
    solve() {
        const strategies = [this.strategyFindSoleRemainingPossibility, this.strategyOnlyOptionInRow, this.strategyOnlyOptionInColumn, this.strategyOnlyOptionInBox]
        let madeProgress: any = true;
        while (madeProgress) {
            madeProgress = null;
            this.calculateAllPossibilities();
            for (var i = 0; i < strategies.length; i++) {
                madeProgress = strategies[i].bind(this)();
                if (madeProgress) {
                    this.addGuess(madeProgress.id, madeProgress.answer)
                    break;
                }
            }
        }
    }

    replacePossibilitiesWithSolverPossibilities() {
        this.cells.forEach((cell) => {
            cell.replacePossibilitiesWithSolverPossibilities();
        })
    }

    /**
     * Iterate through the strategies until one of them is able to find the answer for a cell.
     * @returns
     */
    hint() {
        const strategies = [this.strategyFindSoleRemainingPossibility, this.strategyOnlyOptionInRow, this.strategyOnlyOptionInColumn, this.strategyOnlyOptionInBox]
        this.calculateAllPossibilities();
        let madeProgress = null;
        for (var i = 0; i < strategies.length; i++) {
            madeProgress = strategies[i].bind(this)();
            if (madeProgress) {
                return madeProgress
            }
        }
        return null;
    }
}

window.SudokuGame = SudokuGame;

var AllPossibilities = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F', 'G']

class Cell {
    /**
     * The unique id of the cell from 0 to (size * size) - 1
     * @type {number}
     */
    id: number;

    /**
     * The 0-index row the cell is in
     * @type {number}
     */
    row: number;

    /**
     * The 0-index column the cell is in
     * @type {number}
     */
    column: number;

    /**
     * The 0-index box the cell is in
     * @type {number}
     */
    box: number;

    /**
     * The user defined possibilities
     * @type {Set<string>}
     */
    possibilities: Set<string> = new Set();

    /**
     * possibilities used for the solver to determine a hint or to solve the puzzle
     */
    solverPossibilites: Set<string> = new Set();

    /**
     * The users inputted answer.
     * @type {string}
     */
    guess: string | null = null;

    /**
     * The correct answer for this cell
     * @type {string}
     */
    answer: string;

    /**
     * Is this cell a given cell for the puzzle
     * @type {boolean}
     */
    isGiven: boolean = false;

    /** 
     * Set to true if this cell is marked as a hint
     * @type {boolean}
     */
    isHint: boolean = false;

    constructor() {
    }

    /**
     * Clear the guess and possibilities.
     * @returns
     */
    clear() {
        if (this.isGiven) {
            return;
        }
        this.guess = null;
        this.possibilities.clear();
    }
    /**
     * Clear all possibilities
     */
    resetPossibilities() {
        this.possibilities.clear();
    }

    /**
     * The guess is correct for this cell
     * @returns
     */
    isCorrect() {
        return this.guess && this.guess == this.answer
    }
    /**
     * There is a guess for this cell and it does not match the answer
     * @returns
     */
    isWrong() {
        return this.guess && this.guess != this.answer
    }

    /**
     * Set the guess for the cell
     * @param {string} guess
     * @returns true if the guess is correct, false otherwise
     */
    addGuess(guess) {
        if (guess == ""){
            return;
        }
        guess = guess.toString()

        this.guess = guess;
        return this.guess == this.answer;
    }

    /**
     * Returns true if this cell as user specified possibilities
     * @returns
     */
    hasPossibilities() {
        return this.possibilities.size > 0;
    }
    /**
     * Adds the possibility or removes it if it is already in the set.
     * @param {string} possibility
     */
    togglePossibility(possibility) {
        possibility = possibility.toString()
        if (this.possibilities.has(possibility)) {
            this.possibilities.delete(possibility)
        } else {
            this.possibilities.add(possibility)
        }
    }
    /**
     * Removes the possibility
     * @param {string} possibility
     */
    removePossibility(possibility) {
        this.possibilities.delete(possibility.toString())
    }

    replacePossibilitiesWithSolverPossibilities() {
        // clone the solver possibilities into the new set, so they don't
        // end up sharing a reference
        this.possibilities = new Set(this.solverPossibilites);
    }

    /**
     * Converts the possibilites to an array and sorts for easier reading
     * @returns
     */
    getPossibilitiesArray() {
        return Array.from(this.possibilities).sort();
    }

    /**
     * Converts the cell to a string
     * @returns
     */
    serialize() {
        if (this.guess) {
            return this.guess;
        } else if (this.possibilities.size > 0) {
            return 'P' + Array.from(this.possibilities).join('')
        }
        return ' '
    }
    /**
     * Renders the cell
     * @param {{possibilitiesInGrid: boolean,showIncorrectMoves: boolean, showSolution:boolean }} param0
     * @returns
     */
    render({ activeCellId, highlightActiveCell, possibilitiesInGrid, showIncorrectMoves, showSolution, gridBoxWidth, gridBoxHeight, highlightActiveNumber, activeNumber }) {
        // If we aren't highlighting the active number then just set it to null
        if (!highlightActiveNumber) {
            activeNumber = null;
        }

        if (this.isGiven) {
            return this.renderGiven({ highlightActiveNumber, activeNumber, activeCellId, highlightActiveCell });
        } else if (showSolution) {
            return this.renderAnswer();
        } else if (this.isHint) {
            return this.renderHint()
        } else if (this.guess) {
            return this.renderGuess({ showIncorrectMoves, activeNumber, activeCellId, highlightActiveCell });
        } else {
            return this.renderPossibilities({ possibilitiesInGrid, gridBoxWidth, gridBoxHeight, activeNumber, activeCellId, highlightActiveCell });
        }

    }
    /**
     * Render a given cell
     * @returns
     */
    renderGiven({ highlightActiveNumber, activeNumber, activeCellId, highlightActiveCell }) {
        let numberStyle = "font-weight:700;"
        if (highlightActiveNumber) {
            numberStyle = this.answer == activeNumber ? "font-weight:900;" : ""
        }

        const color = this.getCellColors({ activeCellId, highlightActiveCell });

        return `
        <div style="display: flex; 
            justify-content: center;
            align-items: center;
            width:100%; height:100%;
            ${color}">
                <div style="margin: auto;  ${numberStyle} text-align: center;">${this.guess}</div>
            </div>`
    }

    /**
     * Render an answer
     * @returns
     */
    renderAnswer() {
        return `
        <div style="display: flex;
            justify-content: center;
            align-items: center;
            width:100%; height:100%;>
                <div style="margin: auto; text-align: center;">${this.answer}</div>
            </div>`
    }

    /**
     * Render a guess
     * @param {boolean} showIncorrectMoves
     * @returns
     */
    renderGuess({ showIncorrectMoves, activeNumber, activeCellId, highlightActiveCell }) {
        let activeNumberStyle = this.guess == activeNumber ? "font-weight:900;" : "";
        let style = this.getCellColors({ activeCellId, highlightActiveCell, showIncorrectMoves });
        //TODO: handle incorrect active
        return `
        <div style="display: flex;
            justify-content: center;
            align-items: center;
            width:100%; height:100%;
            ${style}">
                <div style="margin: auto; ${activeNumberStyle} text-align: center;">${this.guess}</div>
            </div>`
    }

    /**
    * Render a hint
    * @param {boolean} showIncorrectMoves
    * @returns
    */
    renderHint() {
        return `
        <div style="display: flex;
            justify-content: center;
            align-items: center;
            width:100%; height:100%;
            background-color: #00ccff;"">
            </div>`
    }
    /**
     * Render possibilites either in grid or list format
     * @param {boolean} possibilitiesInGrid
     * @returns
     */
    renderPossibilities({ possibilitiesInGrid, gridBoxHeight, gridBoxWidth, activeNumber, activeCellId, highlightActiveCell }) {
        return possibilitiesInGrid ? this.renderGridPossibilities({ activeCellId, gridBoxHeight, gridBoxWidth, activeNumber, highlightActiveCell }) : this.renderListPossibilities({ activeCellId, activeNumber, highlightActiveCell })
    }
    /**
     * render possibilities in list form.
     * @returns
     */
    renderListPossibilities({ activeNumber, activeCellId, highlightActiveCell }) {
        const possibilities = this.getPossibilitiesArray().map((val) => {
            const activeNumberStyle = val == activeNumber ? "font-weight:900;" : ""
            return `<span style="${activeNumberStyle}">${val}</span>`
        }).join("&nbsp")
        const color = this.getCellColors({ activeCellId, highlightActiveCell });
        return `<div class="POSSDIV" style="height:100%; width:100%; ${color}; overflow-wrap:anywhere; text-align: right;">${possibilities}&nbsp</div>`
    }

    getPossibilitityText(number, activeNumber) {
        number = number.toString()
        if (!this.possibilities.has(number)) {
            return '&nbsp;'
        }
        const activeNumberStyle = number == activeNumber ? "font-weight:900;" : ""
        return `<span style="${activeNumberStyle}">${number}</span>`
    }

    renderGridPossibilities({ gridBoxHeight, gridBoxWidth, activeNumber, activeCellId, highlightActiveCell }) {

        const tableRows: string[] = [];
        const tdWidth = Math.floor(100 / gridBoxWidth);
        for (var i = 0; i < gridBoxHeight; i++) {
            const row: string[] = [];
            for (var j = 0; j < gridBoxWidth; j++) {
                const val = AllPossibilities[i * gridBoxWidth + j];
                const td = ` <td align="center" width="${tdWidth}%">${this.getPossibilitityText(val, activeNumber)}</td>`
                row.push(td);
            }
            tableRows.push(`<tr>${row.join("")}</tr>`)
        }

        const color = this.getCellColors({ activeCellId, highlightActiveCell});
        return `
            <table style="${color}" class="posstable" cellspacing="0" cellpadding="0">
                <tbody>
                  ${tableRows.join("")}
                </tbody>
              </table>
        `
    }

    getCellColors({ activeCellId, highlightActiveCell, showIncorrectMoves = false }) {
        const isActiveCell = this.id == activeCellId && highlightActiveCell;
        const isIncorrectMove = showIncorrectMoves && this.guess && this.guess != this.answer;
        // Given cells
        if (this.isGiven) {
            return isActiveCell ? "background-color:#bbd9b0;" : "background-color:#e9e9e9;";
        // handle incorrect moves
        } else if (isIncorrectMove) {
            return isActiveCell ? "color:red; background-color:#f9b4c0;" : "color:red; background-color:#f7cfd6;";
        }
        //handle default case
        return isActiveCell ? "background-color:#daffce;" : "";
    }
}
